// ============================================================================
// KES OSINT adapter — Stage 1 Discovery + Stage 2 Enrichment
// ============================================================================
// Production-only: panggil server-side proxy /api/apify-{discover,enrich}
// dengan KES OSINT token. NO mock data path.
//
// Actor IDs di-source dari ../actorConfig.js (G-004 abstraction layer).
// Admin bisa swap actor ID tanpa edit file ini.
// ============================================================================

import { STAGE1_ACTOR_MAP, STAGE2_ACTOR_MAP, getStage1Actors, getStage2Actors } from '../actorConfig';
import { recordActorSuccess, recordActorFailure } from '../actorHealth';
import { getAuthHeaders } from '../../auth';

const PROXY_BASE = '/api/apify';

// Stage 1 = STAGE1_ACTOR_MAP (discover), Stage 2 = STAGE2_ACTOR_MAP (enrich).
// Default ke STAGE1 untuk backward-compat caller yang panggil ACTOR_MAP[platform].
const ACTOR_MAP = STAGE1_ACTOR_MAP;

// ============================================================================
// Stage 1 Discovery — REAL Apify, parallel calls per spec § 4.4b
// ============================================================================
//
// Multi-actor support: STAGE1_ACTOR_MAP value bisa array (mis. Facebook =
// 2 actor: pages-scraper + profile-scraper). Untuk platform tsb dispatch
// SEMUA actor parallel, merge profiles, de-dup by URL.
// ============================================================================
export async function apifyDiscover(subject, platforms) {
  // Single actor call — return { actor, profiles, error }
  // G-005: Record success/failure events untuk actor health monitoring
  const callActor = async (actor, platform) => {
    const start = Date.now();
    try {
      const res = await fetch(`${PROXY_BASE}-discover`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          actor, platform,
          subject_name:  subject.nama_lengkap,
          subject_photo: subject.foto_url,
          username_hint: subject.username || null,
          max_results:   5,
        }),
        signal: AbortSignal.timeout(85000),
      });
      const duration = Date.now() - start;

      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.warn(`[kes-osint] ${platform}/${actor} HTTP ${res.status}:`, text.slice(0, 200));
        recordActorFailure(platform, actor, `HTTP ${res.status}`, duration);
        return { actor, profiles: [], error: `HTTP ${res.status}` };
      }

      const data = await res.json();
      // Diagnostic console — visible di F12 untuk debug demo
      console.log(`[OSINT] ${platform}/${actor}: HTTP ${res.status}, ${data.profiles?.length || 0} profiles, ${duration}ms`);
      if ((data.profiles?.length || 0) === 0) {
        console.log(`[OSINT] ${platform} raw response keys:`, Object.keys(data));
        if (data.error) console.warn(`[OSINT] ${platform} error:`, data.error);
      }
      const profiles = (data.profiles || []).map((profile) => ({
        platform: profile.platform || platform, // Google search detects per-result platform
        handle:         profile.username,
        url:            profile.url,
        display_name:   profile.full_name || profile.name || profile.title,
        thumbnail_url:  profile.profile_pic_url,
        description:    profile.description,
        source_actor:   actor, // tracking actor mana yang return profil ini
        raw:            profile.raw || profile,
      }));
      // 0 results adalah hasil VALID (subject tidak ada di platform tsb), BUKAN
      // actor crash. Tetap record success — actor working dengan output empty.
      // Sebelumnya: recordActorFailure dengan reason 'zero_results' → menyebabkan
      // badge FAILING menyala untuk subject minim digital footprint (UX bug).
      recordActorSuccess(platform, actor, duration);
      return { actor, profiles, error: null };
    } catch (err) {
      const duration = Date.now() - start;
      console.warn(`[kes-osint] ${platform}/${actor} fail:`, err.message);
      recordActorFailure(platform, actor, err.message, duration);
      return { actor, profiles: [], error: err.message };
    }
  };

  // Per platform: dispatch 1 atau N actor parallel, merge results.
  const callOne = async (platform) => {
    const actors = getStage1Actors(platform);
    if (actors.length === 0) return { platform, profiles: [], error: 'no actor' };

    const actorResults = await Promise.allSettled(actors.map(a => callActor(a, platform)));

    // Merge profiles from all actors, de-dup by URL
    const seenUrls = new Set();
    const merged = [];
    const errs = [];
    for (const r of actorResults) {
      if (r.status === 'fulfilled') {
        if (r.value.error && r.value.profiles.length === 0) errs.push(`${r.value.actor}:${r.value.error}`);
        for (const p of r.value.profiles) {
          if (p.url && !seenUrls.has(p.url)) {
            seenUrls.add(p.url);
            merged.push(p);
          }
        }
      } else {
        errs.push(`reject:${r.reason?.message || r.reason}`);
      }
    }
    return {
      platform,
      profiles: merged,
      error: merged.length === 0 && errs.length > 0 ? errs.join(' | ') : null,
    };
  };

  // PARALLEL — Promise.allSettled supaya 1 platform fail tidak block yang lain
  const settled = await Promise.allSettled(platforms.map(callOne));

  const results = [];
  const errors  = [];
  for (const s of settled) {
    if (s.status === 'fulfilled') {
      const v = s.value;
      results.push(...v.profiles);
      if (v.error && v.profiles.length === 0) errors.push(`${v.platform}: ${v.error}`);
    } else {
      errors.push(`promise rejected: ${s.reason?.message || s.reason}`);
    }
  }

  // Per spec § 4.4b "Stage 1 found no candidate profiles" — tidak failure, just empty
  if (results.length === 0 && errors.length > 0) {
    console.warn('[kes-osint] all platforms failed:', errors.join(', '));
  }
  return results;
}

// ============================================================================
// Stage 2 — Enrichment per kandidat
// ============================================================================
//
// Output finding shape (untuk render di OsintTab.jsx):
//   social_media → render dari signal_explain (caption-based)
//   web/media_mention → render dari raw_data.{publisher, headline, snippet, published_at}
//   public_record → render dari raw_data.{authority, record_type, key_data, recorded_at}
//
// Apify actor return per-platform shape — kita map ke struktur shared di atas.
// ============================================================================
export async function apifyEnrich(candidate, dataTypes = ['photos', 'posts', 'mentions']) {
  // Stage 2 actors — bisa multi-actor parallel (Facebook = pages + profile).
  // Boleh beda dari Stage 1 (mis. web → website-content-crawler).
  const actors = getStage2Actors(candidate.platform);
  if (actors.length === 0) return [];

  // Single actor call helper — G-005 record health events
  const enrichOne = async (actor) => {
    const start = Date.now();
    try {
      const res = await fetch(`${PROXY_BASE}-enrich`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          actor,
          platform:     candidate.platform,
          profile_url:  candidate.profile_url,
          handle:       candidate.profile_handle,
          subject_name: candidate.subject_name || null,
          data_types:   dataTypes,
          max_photos:   20,
        }),
        signal: AbortSignal.timeout(60000),
      });
      const duration = Date.now() - start;

      if (!res.ok) {
        console.warn(`[kes-osint] enrich ${candidate.platform}/${actor} failed:`, res.status);
        recordActorFailure(candidate.platform, actor, `HTTP ${res.status}`, duration);
        return [];
      }

      const data = await res.json();
      const items = (data.items || []).map(item => ({ ...item, _source_actor: actor }));
      // 0 items = hasil valid (no enrichment data available), bukan failure
      recordActorSuccess(candidate.platform, actor, duration);
      return items;
    } catch (err) {
      const duration = Date.now() - start;
      console.warn(`[kes-osint] enrich ${candidate.platform}/${actor} error:`, err.message);
      recordActorFailure(candidate.platform, actor, err.message, duration);
      return [];
    }
  };

  try {
    // Dispatch SEMUA actor parallel, merge items, de-dup by URL
    const itemBatches = await Promise.allSettled(actors.map(enrichOne));
    const seenUrls = new Set();
    const items = [];
    for (const r of itemBatches) {
      if (r.status !== 'fulfilled') continue;
      for (const it of r.value) {
        const key = it.url || it.postUrl || JSON.stringify(it).slice(0, 80);
        if (!seenUrls.has(key)) { seenUrls.add(key); items.push(it); }
      }
    }

    const findings = [];

    for (const item of items) {
      const platform = candidate.platform;

      // Detect source_type per platform
      let sourceType = 'social_media';
      if (platform === 'news') sourceType = 'media_mention';
      else if (platform === 'web') sourceType = 'web';
      else if (platform === 'court' || platform === 'gov') sourceType = 'public_record';
      else if (item.type === 'news' || item.type === 'article') sourceType = 'media_mention';

      // Map Apify item ke render shape sesuai source_type
      let raw_data;
      if (sourceType === 'media_mention' || sourceType === 'web') {
        raw_data = {
          publisher:    item.publisher || (item.url ? safeHostname(item.url) : 'KES OSINT'),
          headline:     item.title || item.headline || item.caption || item.text,
          snippet:      item.description || item.snippet || item.text,
          published_at: item.publishedAt || item.timestamp || item.createdAt,
          ...item,
        };
      } else if (sourceType === 'public_record') {
        raw_data = {
          authority:    item.authority || item.court || item.publisher || 'Public Record',
          record_type:  item.record_type || item.case_type || 'Record',
          key_data:     item.key_data || item.summary || item.text || item.title,
          recorded_at:  item.recorded_at || item.publishedAt || item.timestamp,
          ...item,
        };
      } else {
        // social_media
        raw_data = {
          post_id:    item.id || item.postId || item.url,
          text:       item.caption || item.text || item.title,
          timestamp:  item.timestamp || item.publishedAt || item.createdAt,
          likes:      item.likes ?? item.likeCount,
          comments:   item.comments ?? item.commentCount,
          image_urls: item.image_urls || (item.imageUrl ? [item.imageUrl] : []),
          ...item,
        };
      }

      findings.push({
        url:            item.url || candidate.profile_url,
        platform,
        source_type:    sourceType,
        confidence:     candidate.confidence,
        match_signals:  candidate.match_signals,
        signal_explain: `${platform} via KES OSINT · ${(item.caption || item.text || item.title || 'data retrieved').toString().slice(0, 200)}`.slice(0, 240),
        raw_data,
      });
    }
    return findings;
  } catch (err) {
    console.warn(`[kes-osint] enrich ${candidate.platform} error:`, err.message);
    return [];
  }
}

function safeHostname(url) {
  try { return new URL(url).hostname; } catch { return ''; }
}
